import AddIcon from "@mui/icons-material/Add";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import HistoryIcon from "@mui/icons-material/History";
import LoupeIcon from "@mui/icons-material/Loupe";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import {PuiGrid, PuiSearchPage, PuiTable, PuiTableAction} from "@portnet/ui";
import download from "downloadjs";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {generatePath, useNavigate} from "react-router-dom";
import AppAutocomplete from "../../../../common/components/inputs/AppAutocomplete";
import AppSimpleReferentielField from "../../../../common/components/inputs/AppSimpleReferentielField";
import AppTextField from "../../../../common/components/inputs/AppTextField";
import AppButton from "../../../../common/components/ui/AppButton";
import AppDialog from "../../../../common/components/ui/AppDialog";
import AppSection from "../../../../common/components/ui/AppSection";
import {
    DETAIL_CONSULTATION_PACKAGES_PAGE_ROUTE,
    MODIFICATION_PACKAGE_PAGE_ROUTE,
    NOUVEAU_PACKAGE_PAGE_ROUTE,
    NOUVELLE_SOUSCRIPTION_PAGE_ROUTE,
} from "../../../../common/constants/routerConstants";
import useLocalReferentiel from "../../../../common/hooks/useLocalReferentiel";
import usePagination from "../../../../common/hooks/usePagination";
import * as localReferentielKeys from "../../../../common/localReferentiel/others/localReferentielKeys";
import {formatDataForSearch} from "../../../../common/utils/helpers";
import * as consultationPackageFormNames from "../others/consultationPackagesFormNames";
import {consultationPackageInitialValues} from "../others/consultationPackagesInitialValues";
import {
    consultationPackageTableColumns,
    historiquePackageTableColumns,
} from "../others/consultationPackagesTableColumns";
import {consultationPackageValidationSchema} from "../others/consultationPackagesValidationSchemas";
import {
    excelExport,
    getConsultationPackagesListAction,
    getHistoriquePackageAction,
    getHistoriquePackageReinitializationAction,
    printReinitialization,
} from "../state/consultationPackagesActions";

const historiqueDialogInitialState = {
  isOpen: false,
  packageData: null,
  historiqueData: [],
};

const historiqueDialogReducer = (state, { type, payload }) => {
  switch (type) {
    case "CLOSE":
      return { ...historiqueDialogInitialState };
    case "OPEN":
      return { ...state, isOpen: true, packageData: { ...payload } };
    case "SET_HISTORIQUE_DATA":
      return { ...state, isOpen: true, historiqueData: [...payload] };
    default:
      return state;
  }
};

const ConsultationPackagesPage = ({ type }) => {
  const getPageTitleElementsByType = (type) => {
    return {
      title:
        type === "consultation"
          ? "Consultation des packages"
          : type === "gestion"
          ? "Gestion des packages"
          : "",
      icon:
        type === "consultation" ? (
          <FormatListBulletedIcon />
        ) : type === "gestion" ? (
          <ManageSearchIcon />
        ) : (
          ""
        ),
      trace:
        type === "consultation"
          ? ["Accueil", "Consultation des packages"]
          : type === "gestion"
          ? ["Accueil", "Gestion des packages"]
          : undefined,
    };
  };

  const NavigationActions = () => {
    const { user } = useSelector((state) => state.auth.login);
    const navigate = useNavigate();
    return (
      <PuiGrid container>
        <PuiGrid item>
          <AppButton
            loading={isFileToPrintLoading}
            disabled={isConsultationPackagesListLoading}
            loadingPosition="start"
            startIcon={<FileDownloadIcon />}
            color="tertiary"
            onClick={excelExportHandler}
          >
            Exporter
          </AppButton>
        </PuiGrid>
        {user.roles.includes("ROLE_VUPORTUARIA_ADMIN_TARIF") && (
          <PuiGrid item>
            <AppButton
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                navigate(NOUVEAU_PACKAGE_PAGE_ROUTE);
              }}
            >
              Nouveau package
            </AppButton>
          </PuiGrid>
        )}
      </PuiGrid>
    );
  };

  const [historiqueDialogState, historiqueDialogDispatch] = React.useReducer(
    historiqueDialogReducer,
    historiqueDialogInitialState
  );

  const { isLoading: isDeletePackageLoading } = useSelector(
    (state) => state.packages.consultationPackages.deletePackage
  );

  const {
    content: historiquePackage,
    isLoading: isHistoriquePackageLoading,
    isSuccessed: isHistoriquePackageSuccessed,
  } = useSelector(
    (state) => state.packages.consultationPackages.historiquePackage
  );
  const {
    content: fileToPrintContent,
    type: fileToPrintType,
    fileName: fileToPrintName,
    isLoading: isFileToPrintLoading,
    isSuccessed: isFileToPrintSuccessed,
    hasError: hasFileToPrintError,
  } = useSelector((state) => state.packages.consultationPackages.print);

  React.useEffect(() => {
    if (hasFileToPrintError) {
      dispatch(printReinitialization());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFileToPrintError]);

  React.useEffect(() => {
    if (isFileToPrintSuccessed) {
      download(
        `data:${`${fileToPrintType.type}/${fileToPrintType.subtype}`};base64,${fileToPrintContent}`,
        fileToPrintName,
        `${fileToPrintType.type}/${fileToPrintType.subtype}`
      );
      dispatch(printReinitialization());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFileToPrintSuccessed]);
  const {
    content: consultationPackagesList,
    totalElements: consultationPackagesListTotalElements,
    isLoading: isConsultationPackagesListLoading,
  } = useSelector(
    (state) => state.packages.consultationPackages.consultationPackagesList
  );

  const [pageTitleElements, setPageTitleElements] = React.useState({
    title: "",
    icon: <></>,
    trace: [],
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth.login);

  const [paginationState, changePage] = usePagination(10);

  const [filtersState, setFiltersState] = React.useState({
    ...consultationPackageInitialValues,
  });

  const [rolesOperateurOptions] = useLocalReferentiel([
    localReferentielKeys.ROLES_OPERATEUR_LOCAL_REFERENTIEL_KEY,
  ]);

  const historiqueClickHandler = (row) => {
    historiqueDialogDispatch({ type: "OPEN", payload: row });
    dispatch(getHistoriquePackageAction(row.id));
  };

  const excelExportHandler = () => {
    const searchData = formatDataForSearch(filtersState, paginationState, {
      direction: "DESC",
      sortField: "id",
    });
    dispatch(excelExport(searchData));
  };

  const closeHistoriqueDialogHandler = () => {
    historiqueDialogDispatch({ type: "CLOSE" });
  };
  const navigateToDetail = (row) => {
    navigate(
      generatePath(DETAIL_CONSULTATION_PACKAGES_PAGE_ROUTE, {
        packageId: row.id,
      })
    );
  };

  const navigateToNewSouscription = (row) => {
    navigate(
      generatePath(NOUVELLE_SOUSCRIPTION_PAGE_ROUTE, {
        packageId: row.id,
      })
    );
  };

  const modifierClickHandler = (row) => {
    navigate(
      generatePath(MODIFICATION_PACKAGE_PAGE_ROUTE, {
        packageId: row.id,
      })
    );
  };

  const tableActions = React.useMemo(
    () => [
      <PuiTableAction
        title="Détails"
        hide={type === "gestion"}
        color="primary"
        onClick={navigateToDetail}
      >
        <LoupeIcon />
      </PuiTableAction>,
      <PuiTableAction
        title="Modifier"
        hide={type === "consultation"}
        onClick={modifierClickHandler}
      >
        <EditIcon style={{ color: 'fuchsia' }} />
      </PuiTableAction>,
      <PuiTableAction
        title="Historique"
        onClick={historiqueClickHandler}
        hide={!user.roles.includes("ROLE_VUPORTUARIA_ADMIN_TARIF") }
      >
        <HistoryIcon style={{ color: 'orange' }} />
      </PuiTableAction>,
      <PuiTableAction
        title="Souscrire"
        hideFunc={(row) => row.statut.code === "DESACTIVE" || user.roles.includes("ROLE_VUPORTUARIA_ADMIN") || user.roles.includes("ROLE_VUPORTUARIA_USER_MANAGER") }
        onClick={navigateToNewSouscription}
      >
        <AppRegistrationIcon style={{ color: 'cornflowerblue' }} />
      </PuiTableAction>,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type]
  );

  const pageChangeHandler = (pageNumber) => {
    changePage(pageNumber);
  };

  const submitHandler = (values) => {
    setFiltersState(values);
    changePage(0);
  };

  const fetchPackages = () => {
    const searchData = formatDataForSearch(filtersState, paginationState, {
      direction: "DESC",
      sortField: "id",
    });
    dispatch(getConsultationPackagesListAction(searchData));
  };

  React.useEffect(() => {
    setPageTitleElements(getPageTitleElementsByType(type));
  }, [type]);

  React.useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState, paginationState]);

  React.useEffect(() => {
    if (isHistoriquePackageSuccessed) {
      historiqueDialogDispatch({
        type: "SET_HISTORIQUE_DATA",
        payload: historiquePackage,
      });
      dispatch(getHistoriquePackageReinitializationAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoriquePackageSuccessed]);


  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    flex: 1,
    renderCell: ({ row }) => (
        <>
          {tableActions.map((action, index) => {
            if (action.props.hideFunc && action.props.hideFunc(row)) {
              return null;
            }

            return React.cloneElement(action, { key: index, row: row });
          })}
        </>
    ),
  };
  let modifiedColumns = user.roles.includes("ROLE_VUPORTUARIA_ADMIN_TARIF")
      ? [...consultationPackageTableColumns, actionsColumn]
      : consultationPackageTableColumns.filter(column => column.field !== "roleOperateur").concat(actionsColumn);


  return (
    <>
      <PuiSearchPage
        formik
        formikProps={{
          initialValues: consultationPackageInitialValues,
          validationSchema: consultationPackageValidationSchema,
          onSubmit: submitHandler,
        }}
        title={pageTitleElements.title}
        titleIcon={pageTitleElements.icon}
        trace={pageTitleElements.trace}
        navActions={<NavigationActions />}
        loading={isDeletePackageLoading}
        topNav
        retour={false}
        tableProps={{
          columns: modifiedColumns,
          rows: consultationPackagesList,
          rowCount: consultationPackagesListTotalElements,
          loading: isConsultationPackagesListLoading,
          actions: [],
          page: paginationState.page,
          pageSize: paginationState.size,
          onPageChange: pageChangeHandler,
        }}
      >
        <PuiGrid container>
          <PuiGrid item xs={12} sm={4} md={4}>
            <AppTextField
              label="Code package"
              name={consultationPackageFormNames.CODE_PACKAGE_FORM_NAME}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={4} md={4}>
            <AppAutocomplete
              label="Rôle de l'opérateur"
              name={consultationPackageFormNames.ROLE_OPERATEUR_FORM_NAME}
              options={rolesOperateurOptions.content}
              loading={rolesOperateurOptions.isLoading}
              error={rolesOperateurOptions.hasError}
              helperText={rolesOperateurOptions.error}
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.id}
              disabled={!user.roles.includes("ROLE_VUPORTUARIA_ADMIN_TARIF")}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={4} md={4}>
            <AppSimpleReferentielField
              autocomplete
              searchKey="LISTE_STATUS_PACKAGE"
              label="Statut du package"
              name={consultationPackageFormNames.STATUS_PACKAGE_FORM_NAME}
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.code}
              filterFunc={(option) => option.code !== "DELETED"}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={6} md={3}>
            <AppSimpleReferentielField
              autocomplete
              searchKey="LISTE_PAIEMENT_PACKAGE"
              label="Type de paiement"
              name={consultationPackageFormNames.TYPE_PAIEMENT_FORM_NAME}
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.code}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={6} md={3}>
            <AppSimpleReferentielField
              autocomplete
              searchKey="LISTE_TRANSACTIONS_AUTORISEES"
              label="Transaction autorisée"
              name={
                consultationPackageFormNames.TRANSACTION_AUTORISEE_FORM_NAME
              }
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.code}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={6} md={3}>
            <AppTextField
              label="Nombre de transactions"
              name={consultationPackageFormNames.NOMBRE_TRANSACTIONS_FORM_NAME}
            />
          </PuiGrid>
          <PuiGrid item xs={12} sm={6} md={3}>
            <AppSimpleReferentielField
              autocomplete
              searchKey="LISTE_FRAIS_SERVICE_PACKAGE"
              label="Type de frais de service"
              name={
                consultationPackageFormNames.TYPE_FRAIS_SERVICE_PACKAGE_FORM_NAME
              }
              getOptionLabel={(option) => option.description}
              getOptionValue={(option) => option.code}
              filterFunc={(option) => option.code !== "PAR_TRANCHE"}
            />
          </PuiGrid>
        </PuiGrid>
      </PuiSearchPage>
      <AppDialog
        maxWidth="lg"
        fullWidth
        open={historiqueDialogState.isOpen}
        title="Historique du package"
        onClose={closeHistoriqueDialogHandler}
      >
        <AppSection title="Données du package" sx={{ marginBottom: 2 }}>
          <PuiGrid container>
            <PuiGrid item xs={12} sm={4}>
              <AppTextField
                label="Code"
                consultation
                formik={false}
                value={historiqueDialogState.packageData?.code}
              />
            </PuiGrid>
            <PuiGrid item xs={12} sm={4}>
              <AppTextField
                label="Description"
                consultation
                formik={false}
                value={historiqueDialogState.packageData?.description}
              />
            </PuiGrid>
            <PuiGrid item xs={12} sm={4}>
              <AppTextField
                label="Statut"
                consultation
                formik={false}
                value={historiqueDialogState.packageData?.statut?.description}
              />
            </PuiGrid>
          </PuiGrid>
        </AppSection>
        <AppSection title="Historique des évenements liés au package">
          <PuiTable
            rows={historiqueDialogState.historiqueData}
            columns={historiquePackageTableColumns}
            loading={isHistoriquePackageLoading}
            pageSize={5}
            getRowId={(row) => row.sequence}
          />
        </AppSection>
      </AppDialog>
    </>
  );
};

export default ConsultationPackagesPage;
